<template>
  <Modal
    class-name="department-confirmation-modal"
    title="Confirm Archive"
    :width="500"
    @hide-modal="emit('hide-modal')"
  >
    <div class="mb-2">
      <span>Are you sure you want to archive this department?</span>
    </div>
    <Spinner
      v-if="loadingArchiveConfirmData"
      class="mt-3"
      message="Fetching department information..."
    />
    <div v-else>
      <div v-if="archiveConfirmResults === null">
        Unable to generate list at this time.
      </div>
      <div v-else class="summary my-2">
        <div
          v-if="archiveConfirmResults?.hasNothingToArchive"
          data-cy="noDependentModelsToArchive"
        >
          No technicians or active jobs found.
        </div>
        <div v-else>
          <div
            v-if="archiveConfirmResults?.effected_objects.technicians > 0"
            data-cy="techniciansCount"
          >
            • Archiving this department will archive
            {{ archiveConfirmResults?.effected_objects.technicians }}
            technicians.
          </div>
          <div
            v-if="
              archiveConfirmResults?.effected_objects.service_categories > 0
            "
            data-cy="servicesCategoriesCount"
          >
            • Archiving this department will archive
            {{ archiveConfirmResults?.effected_objects.service_categories }}
            service categories.
          </div>
          <div
            v-if="archiveConfirmResults?.effected_objects.jobs > 0"
            data-cy="jobsCount"
          >
            • There are
            {{ archiveConfirmResults?.effected_objects.jobs }}
            job(s) from this department that will remain active.
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="modal-footer">
        <SeraButton
          class="mr-2"
          text="Cancel"
          type="secondary"
          @click="handleHideArchiveConfirm"
        />
        <SeraButton
          data-cy="archiveConfirmButton"
          type="danger"
          text="Archive"
          @click="handleArchive"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useStore } from "vuex";

import SeraButton from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import Spinner from "@/components/Spinner.vue";
import { Department } from "@/typings/department";
import { DepartmentArchiveConfirm } from "@/typings/department";

const emit = defineEmits(["hide-modal", "archived"]);

const props = defineProps({
  department: {
    type: Object as () => Department,
    required: true,
    default: null
  }
});

const store = useStore();
const archiveConfirmResults = ref<DepartmentArchiveConfirm | null>(null);
const loadingArchiveConfirmData = ref(true);
const isArchiving = ref(false);

onMounted(async () => {
  loadingArchiveConfirmData.value = true;
  try {
    const results = await store.dispatch(
      "archiveDepartmentConfirm",
      props.department!
    );
    archiveConfirmResults.value = results;
  } catch (e) {
    archiveConfirmResults.value = null;
  }
  loadingArchiveConfirmData.value = false;
});

function handleHideArchiveConfirm() {
  emit("hide-modal");
}

async function handleArchive() {
  isArchiving.value = true;
  try {
    await store.dispatch("archiveDepartment", props.department);
    window.notify.success("Department has been archived.");
    emit("hide-modal");
    emit("archived");
  } catch (e) {
    console.error(e);
  }
  isArchiving.value = false;
}
</script>

<style lang="scss">
.summary {
  background: var(--light-gray);
  border: 1px solid var(--gray);
  border-radius: 5px;
  padding: 10px;
}
</style>
